/* The quick character: only the choices a character can't do without, on one page.
   Traits and gear take the class's suggestions; heritage, Experiences and domain cards
   reuse the detailed builder's own steps (library cards, tap-to-use examples).
   Everything else can be filled in later from the sheet's gears or the detailed builder. */

import { useEffect, useState } from 'react';
import type { Catalog } from './catalog';
import { Character, itemName, missingSteps, needs, parseTraits, suggestedGear, withClass } from './model';
import { Chips, Panel, SelectField, ShowNeeded, StepProps, TextField } from './BuilderParts';
import { DomainStep, ElementPanel, HeritageStep, PatronPanel, StancePanel } from './StepsChoose';
import { StrikeTrait } from './StepsNumbers';
import { ExperienceStep, randomName } from './StepsStory';
import { FinishConfirm } from './Builder';

interface Props {
  character: Character;
  catalog: Catalog;
  onChange: (c: Character) => void;
  onFinish: () => void;
  /** Opens the nine-step builder with the choices made so far. */
  onDetailed: () => void;
}

export default function QuickBuild({ character: c, catalog, onChange, onFinish, onDetailed }: Props) {
  const [confirming, setConfirming] = useState(false);
  // After Keep building, the panels still missing a choice light up
  const [checking, setChecking] = useState(false);
  const set = (patch: Partial<Character>) => onChange({ ...c, ...patch });
  const step: StepProps = { c, catalog, set };
  const cls = catalog.cls(c.className);
  const inv = cls?.inventory;
  const missing = missingSteps(c);
  const gear = [c.primary, c.secondary, c.armor].filter(Boolean);

  useEffect(() => {
    if (checking) document.querySelector('.cb-quick .cb-panel--needed')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [checking]);

  const pickClass = (name: string) => {
    const next = withClass(c, name);
    const traits = catalog.cls(name)?.stats?.suggestedTraits;
    onChange({ ...next, ...suggestedGear(next, catalog), traits: traits ? parseTraits(traits) : next.traits });
  };

  return (
    <ShowNeeded.Provider value={checking}>
      <div className="cb cb-quick">
        <p className="cb-step__guide">
          Make the choices below and your character is ready to play. Traits and gear take your class's suggestions; change
          anything later with the gears on your sheet.
        </p>
        <div className="cb-quick__grid">
          <Panel title="Class" needed={needs(c, 'class') || needs(c, 'subclass')}>
            <SelectField label="Class" value={c.className} options={catalog.classes.map((k) => k.name)} placeholder="Choose a class" onChange={pickClass} />
            <SelectField
              label="Subclass"
              value={c.subclass}
              options={Object.keys(cls?.subclasses ?? {})}
              placeholder="Choose a subclass"
              disabled={!cls}
              onChange={(subclass) => set({ subclass, stances: [] })}
            />
            {cls && <p className="cb-hint">{cls.stats?.domains} · Evasion {cls.stats?.evasion} · HP {cls.stats?.hp}</p>}
          </Panel>

          <Panel title="Name" needed={needs(c, 'name')}>
            <TextField label="Name" value={c.name} onChange={(name) => set({ name })} />
            <button type="button" className="cb-btn" onClick={() => set({ name: randomName() })}>Suggest a name</button>
          </Panel>

          {cls && (
            <Panel title="Gear" needed={['potion', 'class item', "Brawler's Strike trait"].some((part) => needs(c, part))}>
              <p className="cb-hint">Suggested for a {cls.name}: {gear.join(', ')}.</p>
              <StrikeTrait c={c} set={set} />
              {inv && (
                <>
                  <p className="cb-field__label">Choose between</p>
                  <Chips label="Potion" options={inv.chooseBetween} display={itemName} isOn={(o) => o === c.potion} onPick={(potion) => set({ potion })} />
                  <p className="cb-field__label">And either</p>
                  <Chips label="Class item" options={inv.andEither} display={itemName} isOn={(o) => o === c.classItem} onPick={(classItem) => set({ classItem })} />
                </>
              )}
            </Panel>
          )}
        </div>

        <StancePanel {...step} />
        <PatronPanel {...step} />
        <ElementPanel {...step} />
        <div className="cb-quick__pair">
          <HeritageStep {...step} />
        </div>
        <ExperienceStep {...step} />
        <Panel title="Domain cards" needed={needs(c, 'two domain cards')}>
          <DomainStep {...step} />
        </Panel>

        <div className="cb-actions cb-quick__actions">
          <button type="button" className="cb-btn" onClick={onDetailed}>Switch to the detailed builder</button>
          <button type="button" className="cb-btn cb-btn--primary" onClick={() => (missing.length ? setConfirming(true) : onFinish())}>Create character</button>
        </div>
        {confirming && (
          <FinishConfirm
            character={c}
            onKeep={() => {
              setConfirming(false);
              setChecking(true);
            }}
            onFinish={onFinish}
          />
        )}
      </div>
    </ShowNeeded.Provider>
  );
}
